import React from "react";
import { motion } from "framer-motion";

// 404 Animated Page - Single-file React component
// Tailwind CSS required in project. Uses framer-motion for subtle motion.

export default function NotFound() {
  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-b from-sky-50 via-white to-indigo-50 p-6">
      <div className="relative max-w-4xl w-full bg-white/60 backdrop-blur-md rounded-3xl shadow-2xl overflow-hidden border border-white/50">
        {/* Decorative SVG background */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 1200 600"
          preserveAspectRatio="none"
          aria-hidden
        >
          <defs>
            <linearGradient id="g1" x1="0" x2="1">
              <stop offset="0%" stopColor="#7dd3fc" stopOpacity="0.12" />
              <stop offset="100%" stopColor="#c7b8ff" stopOpacity="0.10" />
            </linearGradient>
            <filter id="f1" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="40" result="b" />
              <feBlend in="SourceGraphic" in2="b" />
            </filter>
          </defs>

          {/* soft blob left */}
          <motion.path
            initial={{ scale: 0.95, translateX: -20 }}
            animate={{ scale: [0.95, 1.03, 0.98], translateX: [-20, 0, -10] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            d="M0 120 C120 40, 320 20, 480 110 C640 200, 920 160, 1200 80 L1200 600 L0 600 Z"
            fill="url(#g1)"
            filter="url(#f1)"
            opacity="0.9"
          />

          {/* dotted shooting comet */}
          <motion.circle
            cx="920"
            cy="80"
            r="6"
            initial={{ x: -60, y: 0, opacity: 0 }}
            animate={{ x: [0, -40, -200], y: [0, 10, 60], opacity: [0, 1, 0] }}
            transition={{ duration: 5, repeat: Infinity, delay: 0.8 }}
            fill="#7c3aed"
          />

          {/* subtle grid lines */}
          <g opacity="0.05" stroke="#000">
            <path d="M0 300 H1200" strokeWidth="1" />
            <path d="M0 450 H1200" strokeWidth="1" />
          </g>
        </svg>

        <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-6 p-8 md:p-12 items-center">
          <section className="space-y-6 text-center md:text-left">
            <motion.h1
              className="text-6xl md:text-7xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-sky-500"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.7 }}
            >
              404
            </motion.h1>

            <motion.h2
              className="text-xl md:text-2xl font-semibold text-slate-700"
              initial={{ y: -6, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              Oops — page not found
            </motion.h2>

            <motion.p
              className="text-sm md:text-base text-slate-600 max-w-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.25 }}
            >
              The page you’re looking for doesn’t exist or has been moved. Try returning to the
              homepage or check the URL for typos.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 gap-3 justify-center md:justify-start"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.35 }}
            >
              <a
                href="/"
                className="inline-flex items-center justify-center px-5 py-3 rounded-full shadow-md border border-transparent text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500"
                style={{ background: "linear-gradient(90deg,#6366f1 0%,#06b6d4 100%)", color: "white" }}
                aria-label="Go to homepage"
              >
                Go home
              </a>

              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium border border-slate-200 bg-white/60 hover:bg-white"
                aria-label="Contact support"
              >
                Contact support
              </a>
            </motion.div>

            <motion.div
              className="mt-4 text-xs text-slate-500"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.45 }}
            >
              Tip: Press <kbd className="px-2 py-1 rounded bg-slate-100 border">Esc</kbd> to close
            </motion.div>
          </section>

          <aside className="flex items-center justify-center">
            {/* Main SVG illustration (robot/alien with magnifier) */}
            <motion.svg
              width="340"
              height="340"
              viewBox="0 0 600 600"
              initial={{ scale: 0.95, rotate: -6 }}
              animate={{ scale: [0.95, 1.02, 0.99], rotate: [-6, 6, -2] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              role="img"
              aria-label="Illustration of a friendly robot looking for a lost page"
            >
              <defs>
                <linearGradient id="btnGrad" x1="0" x2="1">
                  <stop offset="0%" stopColor="#60a5fa" />
                  <stop offset="100%" stopColor="#a78bfa" />
                </linearGradient>
              </defs>

              <g transform="translate(70,40)">
                {/* body */}
                <motion.rect
                  x="120"
                  y="170"
                  width="260"
                  height="220"
                  rx="32"
                  fill="white"
                  stroke="#e6e9f2"
                  strokeWidth="6"
                  initial={{ y: 180 }}
                  animate={{ y: [180, 170, 185] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                />

                {/* visor */}
                <motion.ellipse
                  cx="250"
                  cy="230"
                  rx="90"
                  ry="40"
                  fill="url(#btnGrad)"
                  opacity="0.95"
                  initial={{ x: -6 }}
                  animate={{ x: [ -6, 6, -2] }}
                  transition={{ duration: 4, repeat: Infinity }}
                />

                {/* magnifying glass */}
                <motion.g
                  transform="translate(320,250)"
                  initial={{ rotate: -20 }}
                  animate={{ rotate: [ -20, 10, -10 ] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <circle cx="40" cy="40" r="44" stroke="#94a3b8" strokeWidth="6" fill="none" />
                  <circle cx="40" cy="40" r="26" fill="#e0f2fe" />
                  <rect x="70" y="70" width="70" height="14" rx="8" transform="rotate(30 70 70)" fill="#c7d2fe" />
                </motion.g>

                {/* small stars */}
                <motion.circle cx="60" cy="50" r="6" fill="#fbbf24" animate={{ y: [0, 6, 0] }} transition={{ duration: 2, repeat: Infinity }} />
                <motion.circle cx="40" cy="120" r="4" fill="#fb7185" animate={{ y: [0, -4, 0] }} transition={{ duration: 2.4, repeat: Infinity }} />

                {/* feet */}
                <rect x="160" y="380" width="64" height="24" rx="8" fill="#eef2ff" />
                <rect x="320" y="380" width="64" height="24" rx="8" fill="#eef2ff" />
              </g>
            </motion.svg>
          </aside>
        </div>

        {/* subtle footer */}
        <footer className="relative z-10 border-t border-white/40 p-4 text-center text-xs text-slate-500">
          <div className="flex items-center justify-center gap-2">
            <span>Want to report a broken link?</span>
            <a href="#" onClick={(e) => e.preventDefault()} className="underline">
              Let us know
            </a>
          </div>
        </footer>
      </div>
    </main>
  );
}
